import {useEffect, useState} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, List, Row} from "antd";
import {
	Button,
	Loading,
	StatusLabel,
	FontAwesomeIcon
} from "~/components";
import {
	InputField,
	InputPriceField,
	SelectUserAll,
	SelectField,
	SelectCustomerAll,
	DateField,
	SelectContractAll
} from "~/components/Forms";

import {useUtilities, useCan} from "~/hooks";
import {useSelector} from "react-redux";
import {currentUserSelector} from "~/features/Auth/authSlice";
import {renderDate} from "../../../../utils";
import {CAREERS} from "../../../../app/constants";

function ContractFormAddEdit({item, onHandleSubmit, openModal, contractPromotions, setContractPromotions}) {

	const {utilities} = useUtilities();

	const canMarketingExtension = useCan('contractMarketingExtension');

	const currentUser = useSelector(currentUserSelector);

	const isEdit = Boolean(item?.id);

	//Lọc dịch vụ theo kênh của nhân viên: NV website (phòng 'sales') chỉ thấy dịch vụ website,
	//NV marketing (phòng 'marketing') chỉ thấy dịch vụ marketing. Phòng ban khác (admin/CEO...) thấy tất cả.
	const userDept = currentUser?.department?.key;
	//Phòng marketing (NV kinh doanh MKT) và phòng gia hạn marketing (NV gia hạn MKT) đều thuộc kênh marketing.
	const serviceChannel = (userDept === 'marketing' || userDept === 'marketingRenewal') ? 'marketing' : (userDept === 'sales' ? 'website' : null);

	const defaultService = serviceChannel === 'marketing'
		? (Object.keys(utilities.services).find(k => utilities.services[k].channel === 'marketing') ?? 'website')
		: 'website';

	const userShareOptions = (isEdit && typeof item.userShare?.firstname != "undefined") ? [{ label : `${item.userShare.firstname} ${item.userShare.lastname}`, value : item.userShare.id }] : [];

	const customerOptions = (isEdit && typeof item.customer?.code != "undefined") ? [{ label : `${item.customer.code} ${item.customer.name} ${item.customer.companyName}`, value : item.customer.id }] : [];

	const [serviceValue, setServiceValue] = useState((item?.service?.key) ? item?.service?.key : defaultService);

	//Option khách hàng được auto-fill từ HĐ nguồn (gia hạn/nâng cấp) để picker hiển thị đúng nhãn.
	const [parentCustomerOption, setParentCustomerOption] = useState(null);

	let serviceOptions = [];

	for (const [key, value] of Object.entries(utilities.services)) {
		//Khi tạo mới: chỉ hiện dịch vụ đúng kênh của nhân viên. Khi sửa: hiện tất cả để giữ giá trị hiện tại.
		if(serviceChannel && !isEdit && (value.channel ?? 'website') !== serviceChannel) continue;
		serviceOptions.push({value : key, label : <StatusLabel small type={value.color}>{value.label}</StatusLabel>})
	}

	let serviceTypeOptions = [];
	for (const [key, value] of Object.entries(utilities.serviceTypes)) {
		serviceTypeOptions.push({value : key, label : <StatusLabel small type={value.color}>{value.label}</StatusLabel>})
	}

	const careerOptions = CAREERS;

	const initialValues = {
		customerId: '',
		userShareId: '',
		name: '',
		domain: '',
		career: '',
		signing: new Date(),
		price: 0,
		budget: 0,
		service: defaultService,
		serviceType: '',
		option: '',
		contractParent: undefined,
	}

	const validationSchema = Yup.object().shape({
		customerId: Yup.string().required('Bạn chưa chọn khách hàng'),
		name: Yup.string().required('Tên hợp đồng không được để trống'),
		domain: Yup.string().when('service', {
			is: ['host', 'email', 'ssl'],
			then: Yup.string().required('Tên miền không được bỏ trống'),
		}),
		career: Yup.string().required('Bạn chưa chọn ngành nghề kinh doanh'),
		signing: Yup.string().required('Bạn chưa chọn ngày ký hợp đồng'),
		price: Yup.number().when('service', {
			is: (val) => utilities.services[val]?.channel === 'marketing',
			then: Yup.number().required('Phí dịch vụ không được để trống'),
			otherwise: Yup.number().required('Giá trị hợp đồng không được để trống'),
		}),
		budget: Yup.number().min(0, 'Ngân sách không hợp lệ'),
		service: Yup.string().required('Bạn chưa chọn dịch vụ hợp đồng'),
		serviceType: Yup.string().required('Bạn chưa chọn loại dịch vụ hợp đồng'),
		option: Yup.string().when('service', {
			is: 'host',
			then: Yup.string().required('Dung lượng host không được bỏ tróng'),
		}).when('service', {
			is: 'email',
			then: Yup.string().required('Loại email không được bỏ tróng'),
		}).when('service', {
			is: 'ssl',
			then: Yup.string().required('Loại chứng chỉ không được bỏ tróng'),
		}),
		contractParent: Yup.mixed().when('serviceType', {
			is: (val) => val === 'extension' || val === 'upgrade',
			then: Yup.mixed().test('required', 'Bạn chưa chọn hợp đồng cần gia hạn / nâng cấp', (value) => !!value),
		}),
	})

	const { control, handleSubmit, formState: { isSubmitting, errors }, reset, setValue } = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const serviceTypeValue = useWatch({ control, name: 'serviceType' });

	//Chỉ HĐ gia hạn / nâng cấp mới cần chọn HĐ nguồn để auto-fill thông tin.
	const needContractParent = serviceTypeValue === 'extension' || serviceTypeValue === 'upgrade';

	//Khi đổi loại HĐ về loại không cần HĐ nguồn → xoá HĐ nguồn + reset khách auto-fill.
	useEffect(() => {
		if(!needContractParent) {
			setValue('contractParent', undefined);
			setParentCustomerOption(null);
		}
	}, [needContractParent]);

	useEffect(() => {
		if(item?.id) {
			initialValues.customerId = item.customerId;
			initialValues.userShareId = item.userShareId;
			initialValues.name = item.name;
			initialValues.domain = item.domain;
			initialValues.career = item.career;
			initialValues.signing = item.signing*1000;
			initialValues.price = item.price;
			initialValues.budget = item.budget ?? 0;
			initialValues.service = item.service.key;
			initialValues.serviceType = item.serviceType.key;
			initialValues.option = item.option;
			initialValues.contractParent = item.contractParent;
			setServiceValue(item.service.key);
			setContractPromotions(item?.promotions ?? []);
		}
		else
		{
			//Mở lại modal "Thêm mới": form vẫn mounted nên phải reset state dịch vụ về mặc định
			//(nếu không, picker HĐ nguồn vẫn tìm theo dịch vụ đã chọn lần trước).
			setServiceValue(defaultService);
			setContractPromotions([])
		}
		//Reset khách auto-fill mỗi lần mở modal (tránh giữ khách của HĐ nguồn lần trước).
		setParentCustomerOption(null);
		reset(initialValues);
	}, [item]);

	const onSubmit = async (data) => {
		await onHandleSubmit(data, isEdit);
	}

	const handleServiceChange = (value) => {
		setServiceValue(value);
		setValue('option', '');
		//Đổi dịch vụ → HĐ nguồn cũ không còn hợp lệ (phải trùng dịch vụ) → reset để chọn lại.
		setValue('contractParent', undefined);
		setParentCustomerOption(null);
	}

	//Auto-fill thông tin từ HĐ nguồn (gia hạn/nâng cấp): khách hàng, tên HĐ, tên miền, ngành nghề.
	const handleParentSelect = (option) => {
		const contract = option?.contract;
		if(!contract) return;

		const customerId = contract.customer?.id ?? contract.customerId;

		setValue('customerId', customerId);
		setValue('name', contract.name ?? '');
		setValue('domain', contract.domain ?? '');
		if(contract.career) setValue('career', contract.career);

		if(contract.customer) {
			setParentCustomerOption({
				label: `${contract.customer.code ?? ''} ${contract.customer.companyName ?? ''} ${contract.customer.name ?? ''}`.trim(),
				value: customerId,
			});
		}
	}

	const renderServiceOption = () => {

		const serviceConfig = utilities.services[serviceValue];

		serviceTypeOptions = serviceTypeOptions.filter((item) => {
			if(!serviceConfig.type.includes(item.value)) return false;
			//NV kinh doanh marketing không được tạo HĐ gia hạn (trừ khi có quyền riêng)
			if(serviceConfig.channel === 'marketing' && item.value === 'extension' && !canMarketingExtension) return false;
			return true;
		});

		//Field phụ theo dịch vụ (dung lượng host / loại SSL / loại email)
		const optionLabelMap = {host: 'Dung lượng', ssl: 'Loại SSL', email: 'Loại Email'};
		const optionLabel = optionLabelMap[serviceValue];

		let optionField = null;
		if(optionLabel) {
			let options = [];
			for (const [key, value] of Object.entries(serviceConfig.options)) {
				options.push({value : key, label : value.label})
			}
			optionField = (
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="option" render={({ field }) => (
						<SelectField label={optionLabel} options={options} errors={errors} {...field}/>
					)}/>
				</Col>
			)
		}

		//Picker HĐ nguồn khi gia hạn / nâng cấp
		const parentLabel = serviceTypeValue === 'upgrade' ? 'HĐ cần nâng cấp' : 'HĐ cần gia hạn';
		const parentChannel = serviceConfig.channel === 'marketing' ? 'marketing' : undefined;

		return (
			<>
				<Row gutter={10}>
					<Col className="gutter-row" span={optionField ? 12 : 24}>
						<Controller control={control} name="serviceType" render={({ field }) => (
							<SelectField label="Loại dịch vụ" options={serviceTypeOptions} errors={errors} {...field}/>
						)}/>
					</Col>
					{optionField}
				</Row>
				{needContractParent && (
					<Controller control={control} name="contractParent" render={({ field }) => (
						<SelectContractAll
							channel={parentChannel}
							service={serviceValue}
							label={parentLabel}
							options={[]}
							errors={errors}
							{...field}
							onChange={(value, option) => {
								field.onChange(value);
								handleParentSelect(option);
							}}
						/>
					)}/>
				)}
			</>
		)
	}

	const onDeletePromotion = (item) =>
	{
		setContractPromotions((prevItems) => prevItems.filter(p => p.id !== item.id));
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<p className="heading">Dịch vụ</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={10}>
					<Controller control={control} name="service" render={({ field }) => (
						<SelectField label="Dịch vụ" options={serviceOptions} errors={errors} {...field} onChange={(value, event) => {
							handleServiceChange(value);
							field.onChange(value, event);
						}}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={14}>
					{renderServiceOption()}
				</Col>
			</Row>
			<p className="heading">Liên quan hợp đồng</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="customerId" render={({ field }) => (
						<SelectCustomerAll label="Khách hàng" options={parentCustomerOption ? [parentCustomerOption] : customerOptions} errors={errors} {...field} />
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="userShareId" render={({ field }) => (
						<SelectUserAll label="Share với nhân viên" options={userShareOptions} errors={errors} {...field} />
					)}/>
				</Col>
			</Row>
			<p className="heading">Thông tin hợp đồng</p>
			<Controller control={control} name="name" render={({field}) => (
				<InputField label="Tên hợp đồng" errors={errors} {...field}/>
			)}/>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="domain" render={({ field }) => (
						<InputField label="Tên miền" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="price" render={({field}) => (
						<InputPriceField label={utilities.services[serviceValue]?.channel === 'marketing' ? "Phí dịch vụ" : "Giá trị hợp đồng"} errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="career" render={({field }) => (
						<SelectField label="Ngành nghề" options={careerOptions} errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="signing" render={({ field }) => (
						<DateField label="Ngày ký" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			{utilities.services[serviceValue]?.hasBudget && (
				<Controller control={control} name="budget" render={({field}) => (
					<InputPriceField label="Ngân sách quảng cáo" errors={errors} {...field}/>
				)}/>
			)}
			<p className="heading">Khuyến mãi <Button onClick={() => openModal('promotion')} background green leftIcon={<FontAwesomeIcon icon="fa-solid fa-plus" />} type="button"></Button> </p>
			<List
				itemLayout="horizontal"
				dataSource={contractPromotions}
				renderItem={item => (
					<List.Item
						actions={[<Button background red small onClick={() => onDeletePromotion(item)}>Xóa</Button> ]}
					>
						<List.Item.Meta
							title={item.name}
							description={renderDate(item.timeStart) + ' - ' + renderDate(item.timeEnd)}
						/>
					</List.Item>
				)}
			/>
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">{isEdit ? 'Cập nhật' : 'Thêm'} hợp đồng</Button>
			</div>
		</form>
	)
}

export default ContractFormAddEdit;